import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import type { CreatePostInput, Paginated, PostDetailDto, PostListItemDto, UpdatePostInput, VoteInput } from '@zoxo/shared';

import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';

/** 커뮤니티 목록 (GET /posts?topic=&mine=1). 최신순 커서, 공고 목록과 같은 무한 스크롤. mine 은 내 글만. */
export function postsQuery(topic?: string, mine = false) {
  return infiniteQueryOptions({
    queryKey: ['posts', topic ?? null, mine ? 'mine' : 'all'],
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams();
      if (topic) p.set('topic', topic);
      if (mine) p.set('mine', '1');
      if (pageParam) p.set('cursor', pageParam);
      const qs = p.toString();
      return api.get<Paginated<PostListItemDto>>(`/posts${qs ? `?${qs}` : ''}`);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.meta.nextCursor ?? undefined,
  });
}

export function usePosts(topic?: string, mine = false) {
  return useInfiniteQuery(postsQuery(topic, mine));
}

export function postDetailKey(id: string, authed: boolean) {
  return ['post', id, authed ? 'auth' : 'anon'] as const;
}

export function postQuery(id: string, authed: boolean) {
  return queryOptions({
    queryKey: postDetailKey(id, authed),
    queryFn: () => api.get<PostDetailDto>(`/posts/${id}`),
  });
}

/** 글 상세 (GET /posts/:id). Suspense — 라우트 loader 가 채운다. 없는 글은 loader 가 404. */
export function usePost(id: string) {
  const { isAuthenticated } = useAuth();
  return useSuspenseQuery(postQuery(id, isAuthenticated));
}

export function useCreatePost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePostInput) => api.post<PostDetailDto>('/posts', input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['posts'] }),
  });
}

export function useUpdatePost(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePostInput) => api.patch<PostDetailDto>(`/posts/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.removeQueries({ queryKey: ['post', id] });
    },
  });
}

export function useDeletePost(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.del<void>(`/posts/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.removeQueries({ queryKey: ['post', id] });
    },
  });
}

/** 설문 투표 (PUT /posts/:id/vote). 상태 설정형 — 같은 값을 다시 보내도 같다. 끝나면 상세를 다시 읽어 결과를 연다. */
export function useVote(postId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: VoteInput) => api.put<void>(`/posts/${postId}/vote`, input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['post', postId] }),
  });
}
